/**
 * Sync Service - Connects PWA to backend for phone <-> PC sync
 *
 * Push: Send local IndexedDB changes to server
 * Pull: Get server changes and merge into IndexedDB
 *
 * All sync operations require userId from Supabase auth session.
 * Data is isolated per user - each user only sees their own data.
 */

import { db, Account, Transaction, Category, ScheduledTransaction, SkippedOccurrence, HoldingsAdjustment, clearAllData } from './db';
import { supabase } from './supabase';
import { isDevFakeAuth, DEV_FAKE_USER_ID } from './devAuth';
import { dataEvents } from './dataEvents';
import { accountService } from './accounts';

// ============================================================
// Sync Mutex - Prevents concurrent sync operations
// ============================================================

let syncInProgress = false;

/**
 * Get current user ID from Supabase auth session.
 * Returns null if not authenticated.
 */
async function getCurrentUserId(): Promise<string | null> {
  // DEV FAKE AUTH (localhost dev only): sync as the fake user via the API's
  // legacy userId fallback (REQUIRE_SYNC_AUTH=false in apps/api/.env.local).
  if (isDevFakeAuth()) return DEV_FAKE_USER_ID;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.user?.id || null;
  } catch (error) {
    console.error('[SYNC] Failed to get user ID:', error);
    return null;
  }
}

/**
 * Get Authorization header for sync API calls.
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  // DEV FAKE AUTH: no real JWT exists; send no header so the local API uses
  // the legacy userId fallback instead of failing token verification.
  if (isDevFakeAuth()) return {};
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  } catch (error) {
    console.error('[SYNC] Failed to get auth token:', error);
    return {};
  }
}

// API port (backend runs on this port)
const API_PORT = 3001;

/**
 * Auto-dedupe categories (case-insensitive).
 * Keeps the first occurrence of each category name+type, deletes duplicates.
 * Called automatically after sync pull to prevent category bloat.
 */
async function dedupeCategories(): Promise<number> {
  try {
    const all = await db.categories.toArray();
    const seen = new Map<string, string>(); // normalized key -> id to keep
    const toDelete: string[] = [];

    for (const cat of all) {
      // BUG-064 FIX: Guard against undefined id
      if (!cat?.id) {
        console.warn('[SYNC] dedupeCategories: category missing id', cat);
        continue;
      }
      // Case-insensitive, trim whitespace
      const key = `${cat.type}:${cat.name.toLowerCase().trim()}`;
      if (seen.has(key)) {
        toDelete.push(cat.id);
      } else {
        seen.set(key, cat.id);
      }
    }

    if (toDelete.length > 0) {
      await db.categories.bulkDelete(toDelete);
      console.log(`[SYNC] Auto-deduped ${toDelete.length} duplicate categories`);
    }

    return toDelete.length;
  } catch (error) {
    console.error('[SYNC] Category dedupe failed:', error);
    return 0;
  }
}

/**
 * Get smart default API URL based on current browser location.
 * - If on localhost → http://localhost:3001
 * - If on LAN IP (e.g., 192.168.50.183) → http://192.168.50.183:3001
 * - This allows mobile devices on same network to sync automatically
 */
function getDefaultApiUrl(): string {
  if (typeof window === 'undefined') {
    return `http://localhost:${API_PORT}`;
  }

  const hostname = window.location.hostname;
  const protocol = window.location.protocol; // http: or https:

  // Use same host as the browser is connected to, just different port
  return `${protocol}//${hostname}:${API_PORT}`;
}

/**
 * Get API URL - Priority order:
 * 1. localStorage override (for testing/ngrok)
 * 2. VITE_API_URL env var (production: https://api.ninjacash.app)
 * 3. Hostname-based default (local dev: same host + port 3001)
 *
 * To update production URL: edit apps/web/.env.production
 */
export function getApiUrl(): string {
  // 1. localStorage override (testing/ngrok)
  const stored = localStorage.getItem('ninjacash-api-url');
  if (stored && stored.trim()) {
    return stored;
  }

  // 2. Environment variable (production)
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl && envUrl.trim()) {
    return envUrl;
  }

  // 3. Hostname-based default (local dev)
  return getDefaultApiUrl();
}

export function setApiUrl(url: string): void {
  localStorage.setItem('ninjacash-api-url', url);
}

export function clearApiUrl(): void {
  localStorage.removeItem('ninjacash-api-url');
}

/**
 * Reset sync state - clears lastSyncAt timestamp.
 * Use when sync is stuck or phone isn't getting data.
 * Next pull will fetch ALL records for this user.
 */
export function resetSyncState(): void {
  localStorage.removeItem('ninjacash-last-sync');
  console.log('[SYNC] Sync state reset - next pull will fetch all data');
}

/**
 * Force a fresh pull ignoring lastSyncAt.
 * Gets ALL records for this user from server.
 */
export async function forceFreshPull(): Promise<{ success: boolean; error?: string; counts?: Record<string, number> }> {
  console.log('[SYNC] Force fresh pull - ignoring lastSyncAt');

  // Clear the timestamp first
  resetSyncState();

  // Now do a normal pull (which will use null lastSyncAt = get everything)
  return pull();
}

// Get last sync timestamp
export function getLastSyncAt(): Date | null {
  const stored = localStorage.getItem('ninjacash-last-sync');
  return stored ? new Date(stored) : null;
}

function setLastSyncAt(date: Date): void {
  localStorage.setItem('ninjacash-last-sync', date.toISOString());
}

// Sync response type
interface SyncResponse {
  success: boolean;
  accounts?: Record<string, unknown>[];
  transactions?: Record<string, unknown>[];
  categories?: Record<string, unknown>[];
  scheduledTransactions?: Record<string, unknown>[];
  skippedOccurrences?: Record<string, unknown>[];
  holdingsAdjustments?: Record<string, unknown>[];
  syncedAt: string;
  error?: string;
}

// Fired when pulled adjustments change the local store. Literal here (not
// imported from holdings.ts) to avoid a sync -> holdings -> syncManager ->
// sync module cycle; must match HOLDINGS_CHANGED_EVENT in holdings.ts.
const HOLDINGS_CHANGED_EVENT_NAME = 'ninjacash-holdings-changed';

// Serialize for API (convert Date to ISO string)
function serializeAccount(acc: Account): Record<string, unknown> {
  return {
    id: acc.id,
    name: acc.name,
    fullName: acc.fullName,
    type: acc.type,
    balance: acc.balance,
    initialBalance: acc.initialBalance ?? 0, // BUG-058 FIX: Sync initial balance
    currency: acc.currency,
    isActive: acc.isActive,
    sortOrder: acc.sortOrder,
    createdAt: acc.createdAt?.toISOString(),
    updatedAt: acc.updatedAt?.toISOString(),
  };
}

function serializeTransaction(tx: Transaction): Record<string, unknown> {
  return {
    id: tx.id,
    accountId: tx.accountId,
    type: tx.type,
    amount: tx.amount,
    currency: tx.currency,
    categoryId: tx.categoryId,
    toAccountId: tx.toAccountId,
    description: tx.description,
    date: tx.date instanceof Date ? tx.date.toISOString() : tx.date,
    tags: tx.tags,
    scheduledTransactionId: tx.scheduledTransactionId, // BUG-032 FIX: Link to recurring schedule
    wasSkipped: tx.wasSkipped, // BUG-032 FIX: Skip status for recurring items
    createdAt: tx.createdAt?.toISOString(),
    updatedAt: tx.updatedAt?.toISOString(),
    deletedAt: tx.deletedAt?.toISOString(), // Soft delete timestamp
  };
}

function serializeCategory(cat: Category): Record<string, unknown> {
  return {
    id: cat.id,
    name: cat.name,
    type: cat.type,
    icon: cat.icon,
    color: cat.color,
    parentId: cat.parentId,
    isActive: cat.isActive,
  };
}

function serializeScheduledTransaction(sched: ScheduledTransaction): Record<string, unknown> {
  return {
    id: sched.id,
    accountId: sched.accountId,
    type: sched.type,
    amount: sched.amount,
    description: sched.description,
    categoryId: sched.categoryId,
    toAccountId: sched.toAccountId,
    frequency: sched.frequency,
    dayOfMonth: sched.dayOfMonth,
    dayOfWeek: sched.dayOfWeek,
    monthOfYear: sched.monthOfYear,
    nextDueDate: sched.nextDueDate instanceof Date ? sched.nextDueDate.toISOString() : sched.nextDueDate,
    lastGeneratedDate: sched.lastGeneratedDate instanceof Date ? sched.lastGeneratedDate.toISOString() : sched.lastGeneratedDate,
    isPaused: sched.isPaused,
    pausedUntil: sched.pausedUntil instanceof Date ? sched.pausedUntil.toISOString() : sched.pausedUntil,
    isIgnoredPermanently: sched.isIgnoredPermanently,
    endDate: sched.endDate instanceof Date ? sched.endDate.toISOString() : sched.endDate,
    autoDetected: sched.autoDetected,
    detectionKey: sched.detectionKey,
    dismissCount: sched.dismissCount,
    deletedAt: sched.deletedAt instanceof Date ? sched.deletedAt.toISOString() : sched.deletedAt,
    createdAt: sched.createdAt?.toISOString(),
    updatedAt: sched.updatedAt?.toISOString(),
  };
}

function serializeHoldingsAdjustment(adj: HoldingsAdjustment): Record<string, unknown> {
  return {
    id: adj.id,
    groupId: adj.groupId,
    date: adj.date instanceof Date ? adj.date.toISOString() : adj.date,
    amount: adj.amount,
    kind: adj.kind,
    note: adj.note,
    deletedAt: adj.deletedAt instanceof Date ? adj.deletedAt.toISOString() : adj.deletedAt,
    createdAt: adj.createdAt?.toISOString(),
    updatedAt: adj.updatedAt?.toISOString(),
  };
}

function serializeSkippedOccurrence(skip: SkippedOccurrence): Record<string, unknown> {
  return {
    id: skip.id,
    scheduledTransactionId: skip.scheduledTransactionId,
    skippedDate: skip.skippedDate instanceof Date ? skip.skippedDate.toISOString() : skip.skippedDate,
    reason: skip.reason,
    createdAt: skip.createdAt?.toISOString(),
  };
}

/**
 * Push pending local changes to server.
 *
 * IMPORTANT: Only pushes records with syncStatus: 'pending'.
 * This prevents overwriting edits from other devices with stale data.
 *
 * After successful push, marks pushed records as 'synced'.
 */
export async function pushAll(): Promise<{ success: boolean; error?: string }> {
  try {
    // Get current user ID
    const userId = await getCurrentUserId();
    if (!userId) {
      return { success: false, error: 'Not authenticated - please sign in' };
    }

    const apiUrl = getApiUrl();

    // Only get records that have local changes (pending sync)
    // This prevents overwriting edits from other devices with stale data
    const pendingAccounts = await db.accounts
      .filter((a) => a.syncStatus === 'pending')
      .toArray();
    const pendingTransactions = await db.transactions
      .filter((t) => t.syncStatus === 'pending')
      .toArray();
    const pendingScheduledTransactions = await db.scheduledTransactions
      .filter((s) => s.syncStatus === 'pending')
      .toArray();
    const pendingHoldingsAdjustments = await db.holdingsAdjustments
      .filter((a) => a.syncStatus === 'pending')
      .toArray();
    // Categories and skipped occurrences don't have syncStatus - always push all (they rarely change)
    const allCategories = await db.categories.toArray();
    const allSkippedOccurrences = await db.skippedOccurrences.toArray();

    const totalPending = pendingAccounts.length + pendingTransactions.length + pendingScheduledTransactions.length + pendingHoldingsAdjustments.length;

    // Skip push if nothing pending (but still push categories and skipped occurrences)
    if (totalPending === 0 && allCategories.length === 0 && allSkippedOccurrences.length === 0) {
      console.log('[SYNC] Nothing to push (no pending changes)');
      return { success: true };
    }

    console.log('[SYNC] Pushing changes:', {
      userId: userId.slice(0, 8) + '...',
      accounts: pendingAccounts.length,
      transactions: pendingTransactions.length,
      scheduledTransactions: pendingScheduledTransactions.length,
      categories: allCategories.length,
      skippedOccurrences: allSkippedOccurrences.length,
      holdingsAdjustments: pendingHoldingsAdjustments.length,
    });

    const response = await fetch(`${apiUrl}/api/sync/push`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await getAuthHeaders()),
      },
      body: JSON.stringify({
        userId,
        accounts: pendingAccounts.map(serializeAccount),
        transactions: pendingTransactions.map(serializeTransaction),
        scheduledTransactions: pendingScheduledTransactions.map(serializeScheduledTransaction),
        categories: allCategories.map(serializeCategory),
        skippedOccurrences: allSkippedOccurrences.map(serializeSkippedOccurrence),
        holdingsAdjustments: pendingHoldingsAdjustments.map(serializeHoldingsAdjustment),
      }),
    });

    const result: SyncResponse = await response.json();

    if (result.success) {
      // BUG-077 FIX: Do NOT advance lastSyncAt on push.
      // lastSyncAt is the pull cursor, so moving it forward here can cause this
      // device to miss older remote changes it has never pulled.
      // Mark pushed records as synced
      const accountIds = pendingAccounts.map((a) => a.id!).filter(Boolean);
      const transactionIds = pendingTransactions.map((t) => t.id!).filter(Boolean);
      const scheduledIds = pendingScheduledTransactions.map((s) => s.id!).filter(Boolean);
      const holdingsIds = pendingHoldingsAdjustments.map((a) => a.id).filter(Boolean);

      if (accountIds.length > 0) {
        await db.accounts.where('id').anyOf(accountIds).modify({ syncStatus: 'synced' });
      }
      if (transactionIds.length > 0) {
        await db.transactions.where('id').anyOf(transactionIds).modify({ syncStatus: 'synced' });
      }
      if (scheduledIds.length > 0) {
        await db.scheduledTransactions.where('id').anyOf(scheduledIds).modify({ syncStatus: 'synced' });
      }
      if (holdingsIds.length > 0) {
        await db.holdingsAdjustments.where('id').anyOf(holdingsIds).modify({ syncStatus: 'synced' });
      }

      console.log('[SYNC] Push complete, marked as synced:', result.syncedAt);
    } else {
      console.error('[SYNC] Push failed:', result.error);
    }

    return { success: result.success, error: result.error };
  } catch (error) {
    console.error('[SYNC] Push failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Push failed',
    };
  }
}

/**
 * Pull changes from server since last sync
 * Requires authenticated user (userId from Supabase session)
 */
export async function pull(): Promise<{ success: boolean; error?: string; counts?: Record<string, number> }> {
  try {
    // Get current user ID
    const userId = await getCurrentUserId();
    if (!userId) {
      return { success: false, error: 'Not authenticated - please sign in' };
    }

    const apiUrl = getApiUrl();
    const lastSyncAt = getLastSyncAt();

    console.log('[SYNC] Pulling since:', lastSyncAt?.toISOString() || 'beginning', 'for user:', userId.slice(0, 8) + '...');

    const response = await fetch(`${apiUrl}/api/sync/pull`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await getAuthHeaders()),
      },
      body: JSON.stringify({
        userId, // Include userId for data isolation
        lastSyncAt: lastSyncAt?.toISOString(),
      }),
    });

    const result: SyncResponse = await response.json();

    if (!result.success) {
      return { success: false, error: result.error };
    }

    // Merge accounts (BUG-012 FIX: wrapped in atomic transaction)
    if (result.accounts?.length) {
      await db.transaction('rw', db.accounts, async () => {
        for (const data of result.accounts!) {
          await db.accounts.put(deserializeAccount(data));
        }
      });
    }

    // Merge categories (BUG-012 FIX: wrapped in atomic transaction)
    if (result.categories?.length) {
      await db.transaction('rw', db.categories, async () => {
        for (const data of result.categories!) {
          await db.categories.put(deserializeCategory(data));
        }
      });
    }

    // Merge transactions (handle soft deletes) (BUG-012 FIX: wrapped in atomic transaction)
    if (result.transactions?.length) {
      await db.transaction('rw', [db.transactions, db.accounts], async () => {
        for (const data of result.transactions!) {
          // If server says this is deleted, hard delete locally
          if (data.deletedAt) {
            // BUG-057 FIX: Do NOT adjust balance here!
            // The server account balance we pulled ALREADY accounts for this deletion.
            // If we adjust again, we double-count the deletion (balance goes wrong by the transaction amount).
            //
            // Previous code (BUG-002) was wrong:
            // - Server balance reflects deletion
            // - We overwrote local balance with server balance
            // - Then we adjusted again → double-adjustment
            //
            // Now we just hard-delete the transaction without balance adjustment.
            await db.transactions.delete(data.id as string);
            console.log('[SYNC] Deleted transaction from server:', data.id);
          } else {
            await db.transactions.put(deserializeTransaction(data));
          }
        }
      });
    }

    // Merge scheduled transactions
    // IMPORTANT: Do NOT hard-delete even if deletedAt is set!
    // The bridge needs to see deleted records (via findByDetectionKey) to know
    // which patterns were dismissed and should NOT be re-created.
    // BUG-053 FIX: Previously hard-deleted records with deletedAt, causing dismissed
    // patterns to come back after logout/login when detection ran again.
    if (result.scheduledTransactions?.length) {
      await db.transaction('rw', db.scheduledTransactions, async () => {
        for (const data of result.scheduledTransactions!) {
          // Always put the record (preserves deletedAt for bridge to check)
          // getAll() and getActive() already filter out deletedAt records
          await db.scheduledTransactions.put(deserializeScheduledTransaction(data));
          if (data.deletedAt) {
            console.log('[SYNC] Preserved deleted scheduled transaction (for bridge):', data.id);
          }
        }
      });
    }

    // Merge skipped occurrences (no soft delete - just upsert)
    if (result.skippedOccurrences?.length) {
      await db.transaction('rw', db.skippedOccurrences, async () => {
        for (const data of result.skippedOccurrences!) {
          await db.skippedOccurrences.put(deserializeSkippedOccurrence(data));
        }
      });
    }

    // Merge holdings adjustments (crypto gains/losses/value syncs).
    // Server deletedAt -> hard delete locally, same pattern as transactions.
    if (result.holdingsAdjustments?.length) {
      await db.transaction('rw', db.holdingsAdjustments, async () => {
        for (const data of result.holdingsAdjustments!) {
          if (data.deletedAt) {
            await db.holdingsAdjustments.delete(data.id as string);
          } else {
            await db.holdingsAdjustments.put(deserializeHoldingsAdjustment(data));
          }
        }
      });
    }

    const counts = {
      accounts: result.accounts?.length || 0,
      categories: result.categories?.length || 0,
      transactions: result.transactions?.length || 0,
      scheduledTransactions: result.scheduledTransactions?.length || 0,
      skippedOccurrences: result.skippedOccurrences?.length || 0,
      holdingsAdjustments: result.holdingsAdjustments?.length || 0,
    };

    const totalReceived = counts.accounts + counts.categories + counts.transactions + counts.scheduledTransactions + counts.skippedOccurrences + counts.holdingsAdjustments;

    // IMPORTANT: Only update lastSyncAt if we received data OR if this was a fresh sync
    // This prevents the "empty sync advances timestamp" bug where:
    // 1. Device A syncs (gets nothing, sets lastSyncAt to X)
    // 2. Device B pushes data (with timestamp T where T < X)
    // 3. Device A syncs again but misses data because T < X
    if (totalReceived > 0 || !lastSyncAt) {
      setLastSyncAt(new Date(result.syncedAt));
      console.log('[SYNC] Pull complete, updated lastSyncAt:', counts);
    } else {
      console.log('[SYNC] Pull complete (no new data, keeping lastSyncAt):', counts);
    }

    // BUG-056 FIX: Reconcile balances after sync
    // When two devices create transactions concurrently, the balance field uses last-write-wins.
    // But transactions merge correctly (both get each other's). This can cause balance to drift
    // from the true calculated value. Reconciliation ensures balance always matches transactions.
    //
    // Only reconcile if we received accounts or transactions (balance might have changed)
    if (counts.transactions > 0 || counts.accounts > 0) {
      try {
        const correctedCount = await accountService.reconcileBalancesAfterSync();
        if (correctedCount > 0) {
          console.log(`[SYNC] Reconciled ${correctedCount} account balance(s)`);
        }
      } catch (reconcileError) {
        // Don't fail the sync if reconciliation fails - log and continue
        console.error('[SYNC] Balance reconciliation failed (non-fatal):', reconcileError);
      }
    }

    // Notify UI to refresh if we received any data
    if (totalReceived > 0) {
      // Transactions affect account balances - emit accounts event when transactions sync
      const shouldRefreshAccounts = counts.transactions > 0 || counts.accounts > 0;
      if (shouldRefreshAccounts) dataEvents.emit('accounts');
      if (counts.transactions > 0) dataEvents.emit('transactions');
      if (counts.categories > 0) {
        // Auto-dedupe categories after sync (case-insensitive)
        // This prevents duplicate categories from accumulating across devices
        await dedupeCategories();
        dataEvents.emit('categories');
      }
      // Scheduled transactions affect overdue items display
      if (counts.scheduledTransactions > 0 || counts.skippedOccurrences > 0) {
        dataEvents.emit('all'); // Refresh everything for schedule changes
      }
      // Crypto page / Accounts crypto card listen for this window event
      if (counts.holdingsAdjustments > 0) {
        try {
          window.dispatchEvent(new Event(HOLDINGS_CHANGED_EVENT_NAME));
        } catch { /* non-browser environment */ }
      }
    }

    return { success: true, counts };
  } catch (error) {
    console.error('[SYNC] Pull failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Pull failed',
    };
  }
}

/**
 * Full sync: Push local, then pull remote
 *
 * IMPORTANT: We save lastSyncAt BEFORE pushing, then use that for pulling.
 * This ensures we don't miss changes from other devices that were pushed
 * before our push but after our last sync.
 *
 * MUTEX: Prevents concurrent sync operations which can cause data races.
 */
export async function fullSync(): Promise<{ success: boolean; error?: string }> {
  // Prevent concurrent sync operations
  if (syncInProgress) {
    console.log('[SYNC] Sync already in progress, skipping');
    return { success: false, error: 'Sync already in progress' };
  }

  syncInProgress = true;
  console.log('[SYNC] Starting full sync...');

  try {
    // Save current lastSyncAt BEFORE pushing
    // This is critical: if we use the timestamp set by push, we'd miss
    // changes from other devices that were pushed before our push
    const savedLastSyncAt = getLastSyncAt();

    // Push first (this will update lastSyncAt, but we'll override it)
    const pushResult = await pushAll();
    if (!pushResult.success) {
      return pushResult;
    }

    // Temporarily restore the saved lastSyncAt for pulling
    // This ensures we get ALL changes since our last actual sync
    if (savedLastSyncAt) {
      localStorage.setItem('ninjacash-last-sync', savedLastSyncAt.toISOString());
    } else {
      localStorage.removeItem('ninjacash-last-sync');
    }

    // Now pull - this will fetch changes since savedLastSyncAt
    // and update lastSyncAt to the new server time
    const pullResult = await pull();
    return { success: pullResult.success, error: pullResult.error };
  } finally {
    syncInProgress = false;
  }
}

/**
 * Check if server is reachable
 */
export async function checkConnection(): Promise<boolean> {
  try {
    const apiUrl = getApiUrl();
    const response = await fetch(`${apiUrl}/health`, {
      method: 'GET',
      signal: AbortSignal.timeout(5000),
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Get sync status from server for current user
 */
export async function getSyncStatus(): Promise<{
  connected: boolean;
  lastSyncAt: string | null;
  counts?: { accounts: number; categories: number; transactions: number };
}> {
  try {
    const apiUrl = getApiUrl();
    const userId = await getCurrentUserId();

    // Include userId in status request for per-user stats
    const url = userId
      ? `${apiUrl}/api/sync/status?userId=${encodeURIComponent(userId)}`
      : `${apiUrl}/api/sync/status`;

    const response = await fetch(url, {
      headers: {
        ...(await getAuthHeaders()),
      },
    });
    const data = await response.json();
    return {
      connected: true,
      lastSyncAt: data.lastSyncAt,
      counts: data.counts,
    };
  } catch {
    return {
      connected: false,
      lastSyncAt: null,
    };
  }
}

// Deserialize from API (convert ISO string to Date)
function deserializeAccount(data: Record<string, unknown>): Account {
  return {
    id: data.id as string,
    name: data.name as string,
    fullName: data.fullName as string | undefined,
    type: data.type as Account['type'],
    balance: data.balance as number,
    initialBalance: (data.initialBalance as number) || 0, // BUG-058 FIX: Preserve initial balance
    currency: (data.currency as string) || 'USD',
    isActive: data.isActive !== false,
    sortOrder: data.sortOrder as number | undefined,
    createdAt: data.createdAt ? new Date(data.createdAt as string) : new Date(),
    updatedAt: data.updatedAt ? new Date(data.updatedAt as string) : new Date(),
    syncStatus: 'synced',
  };
}

function deserializeCategory(data: Record<string, unknown>): Category {
  return {
    id: data.id as string,
    name: data.name as string,
    type: data.type as Category['type'],
    icon: data.icon as string | undefined,
    color: data.color as string | undefined,
    parentId: data.parentId as string | undefined,
    isActive: data.isActive !== false,
  };
}

function deserializeTransaction(data: Record<string, unknown>): Transaction {
  return {
    id: data.id as string,
    accountId: data.accountId as string,
    type: data.type as Transaction['type'],
    amount: data.amount as number,
    currency: (data.currency as string) || 'USD',
    categoryId: data.categoryId as string | undefined,
    toAccountId: data.toAccountId as string | undefined,
    description: (data.description as string) || '',
    date: new Date(data.date as string),
    tags: data.tags as string[] | undefined,
    scheduledTransactionId: data.scheduledTransactionId as string | undefined,
    wasSkipped: data.wasSkipped as boolean | undefined,
    createdAt: data.createdAt ? new Date(data.createdAt as string) : new Date(),
    updatedAt: data.updatedAt ? new Date(data.updatedAt as string) : new Date(),
    deletedAt: data.deletedAt ? new Date(data.deletedAt as string) : undefined, // BUG-001 FIX
    syncStatus: 'synced',
  };
}

function deserializeScheduledTransaction(data: Record<string, unknown>): ScheduledTransaction {
  return {
    id: data.id as string,
    accountId: data.accountId as string,
    type: data.type as ScheduledTransaction['type'],
    amount: data.amount as number,
    description: (data.description as string) || '',
    categoryId: data.categoryId as string | undefined,
    toAccountId: data.toAccountId as string | undefined,
    frequency: data.frequency as ScheduledTransaction['frequency'],
    dayOfMonth: data.dayOfMonth as number | undefined,
    dayOfWeek: data.dayOfWeek as number | undefined,
    monthOfYear: data.monthOfYear as number | undefined,
    nextDueDate: new Date(data.nextDueDate as string),
    lastGeneratedDate: data.lastGeneratedDate ? new Date(data.lastGeneratedDate as string) : undefined,
    isPaused: data.isPaused as boolean || false,
    pausedUntil: data.pausedUntil ? new Date(data.pausedUntil as string) : undefined,
    isIgnoredPermanently: data.isIgnoredPermanently as boolean || false,
    endDate: data.endDate ? new Date(data.endDate as string) : undefined,
    autoDetected: data.autoDetected as boolean || false,
    detectionKey: data.detectionKey as string | undefined,
    dismissCount: (data.dismissCount as number) || 0,
    deletedAt: data.deletedAt ? new Date(data.deletedAt as string) : undefined,
    createdAt: data.createdAt ? new Date(data.createdAt as string) : new Date(),
    updatedAt: data.updatedAt ? new Date(data.updatedAt as string) : new Date(),
    syncStatus: 'synced',
  };
}

function deserializeHoldingsAdjustment(data: Record<string, unknown>): HoldingsAdjustment {
  return {
    id: data.id as string,
    groupId: data.groupId as string,
    date: new Date(data.date as string),
    amount: data.amount as number,
    kind: data.kind as HoldingsAdjustment['kind'],
    note: (data.note as string | null) || undefined,
    deletedAt: data.deletedAt ? new Date(data.deletedAt as string) : undefined,
    createdAt: data.createdAt ? new Date(data.createdAt as string) : new Date(),
    updatedAt: data.updatedAt ? new Date(data.updatedAt as string) : new Date(),
    syncStatus: 'synced',
  };
}

function deserializeSkippedOccurrence(data: Record<string, unknown>): SkippedOccurrence {
  return {
    id: data.id as string,
    scheduledTransactionId: data.scheduledTransactionId as string,
    skippedDate: new Date(data.skippedDate as string),
    reason: data.reason as string | undefined,
    createdAt: data.createdAt ? new Date(data.createdAt as string) : new Date(),
  };
}

/**
 * Clear local data on sign out.
 *
 * CRITICAL for data isolation: When user signs out, we must clear local data
 * to prevent the next user from seeing/pushing the previous user's data.
 *
 * Clears:
 * - IndexedDB (accounts, transactions, categories, etc.)
 * - Sync state (lastSyncAt timestamp)
 *
 * Does NOT clear:
 * - API URL setting (not user-specific)
 * - Sync settings (autoSyncEnabled, etc.)
 */
export async function clearLocalDataOnSignOut(): Promise<void> {
  console.log('[SYNC] Clearing local data on sign out...');

  try {
    // Clear IndexedDB
    await clearAllData();
    console.log('[SYNC] IndexedDB cleared');

    // Clear sync timestamp (next login will do fresh sync)
    resetSyncState();
    console.log('[SYNC] Sync state cleared');

    console.log('[SYNC] Local data cleared for sign out');
  } catch (error) {
    console.error('[SYNC] Failed to clear local data on sign out:', error);
    // Don't throw - sign out should still proceed
  }
}

/**
 * Delete all user data (local + server)
 * GDPR/Play Store compliance - complete account deletion
 *
 * Steps:
 * 1. Delete server data first (while we still have userId)
 * 2. Clear local IndexedDB
 * 3. Clear sync state and API settings
 *
 * Returns success if local data is cleared (minimum requirement).
 * Server deletion is attempted but not required for success.
 *
 * IMPORTANT: Caller must sign out from Supabase after this completes.
 */
export async function deleteAccount(): Promise<{
  success: boolean;
  error?: string;
  localCleared: boolean;
  serverCleared: boolean;
}> {
  console.log('[SYNC] Starting account deletion...');

  let localCleared = false;
  let serverCleared = false;
  let serverError: string | undefined;

  try {
    // Step 1: Try to delete server data first (while we still have userId)
    try {
      const userId = await getCurrentUserId();
      if (userId) {
        const apiUrl = getApiUrl();
        console.log('[SYNC] Deleting server data for user:', userId.slice(0, 8) + '...');

        const response = await fetch(`${apiUrl}/api/sync/delete-account`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(await getAuthHeaders()),
          },
          body: JSON.stringify({ userId }),
          signal: AbortSignal.timeout(10000), // 10 second timeout
        });

        // Check HTTP status first
        if (!response.ok) {
          throw new Error(`Server returned ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();

        if (result.success) {
          serverCleared = true;
          console.log('[SYNC] Server data deleted successfully');
        } else {
          serverError = result.error || 'Server deletion failed';
          console.error('[SYNC] Server deletion failed:', serverError);
        }
      } else {
        serverError = 'Not authenticated - no userId available';
        console.warn('[SYNC] No userId - skipping server deletion (user not logged in or session expired)');
      }
    } catch (error) {
      serverError = error instanceof Error ? error.message : 'Network error';
      console.error('[SYNC] Server deletion error (continuing with local delete):', error);
      // Don't throw - continue with local deletion
    }

    // Step 2: Clear local IndexedDB data (CRITICAL - always do this)
    console.log('[SYNC] Clearing local IndexedDB data...');
    await clearAllData();
    localCleared = true;
    console.log('[SYNC] Local data cleared successfully');

    // Step 3: Clear sync state and API settings
    resetSyncState();
    clearApiUrl();
    console.log('[SYNC] Sync state and API settings cleared');

    // Success if local data is cleared (minimum requirement for account deletion)
    return {
      success: true,
      localCleared,
      serverCleared,
      error: serverError, // Include server error for logging, but still return success
    };
  } catch (error) {
    console.error('[SYNC] Account deletion failed:', error);
    return {
      success: false,
      localCleared,
      serverCleared,
      error: error instanceof Error ? error.message : 'Deletion failed',
    };
  }
}
